import React, { useEffect, useRef, useState } from 'react'
import {
  Eye, Gem, Globe, Heart, Image as ImageIcon, Loader2, Lock, Package, Palette,
  RotateCcw, Send, Trash2, Upload, User
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useProfileStore } from '../store/useProfileStore'
import ConfirmModal from '../../../components/ConfirmModal'
import {
  accentColor,
  cardNickColor,
  cardPrimaryColor,
  cardSecondaryColor,
  COLOR_KEYS,
  contentBgColor,
  isValidHex,
  normalizeHex,
  profileBackground,
  PROFILE_THEME,
  skinCardBgColor,
  skinCardPrimaryColor
} from '../utils/profileColors'
import {
  BG_FITS,
  backgroundImageStyle,
  backgroundOverlayStyle,
  buildBackgroundPayload,
  fromBackground,
  prepareBackgroundFile
} from '../utils/profileBackground'
import { hapticImpact, hapticNotify, hapticSelectionChanged } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'
import useAppEnvironment from '../../../hooks/useAppEnvironment'

const fromColors = (colors) => ({
  nick: colors?.nick ?? null,
  bg: colors?.bg ?? null,
  bg2: colors?.bg2 ?? null,
  bg_angle: Number.isFinite(colors?.bg_angle) ? colors.bg_angle : 180,
  accent: colors?.accent ?? null,
  avatar_frame: colors?.avatar_frame ?? null,
  card_bg: colors?.card_bg ?? null,
  content_bg: colors?.content_bg ?? null,
  text_secondary: colors?.text_secondary ?? null
})

/**
 * Diff для автосохранения: только изменённые ключи (null — валидный сброс к теме).
 * Незавершённый ввод (частичный hex вроде «#FF8») не отправляем — ждём валидное значение.
 * Возвращает null, если отправлять нечего.
 */
const buildColorsPayload = (draft, initial) => {
  const payload = {}
  COLOR_KEYS.forEach((key) => {
    const next = normalizeHex(draft[key])
    const prev = normalizeHex(initial[key])
    if (draft[key] === null || draft[key] === '') {
      // Явный сброс к теме — отправляем null, только если было значение
      if (prev !== null) payload[key] = null
    } else if (next && next !== prev) {
      payload[key] = next
    }
  })
  if (draft.bg_angle !== initial.bg_angle) payload.bg_angle = draft.bg_angle
  return Object.keys(payload).length > 0 ? payload : null
}

const prepareAvatarFile = (file, size) => new Promise((resolve, reject) => {
  const isPng = file.type === 'image/png'
  const url = URL.createObjectURL(file)
  const image = new Image()

  image.onload = () => {
    URL.revokeObjectURL(url)
    const sourceSize = Math.min(image.naturalWidth, image.naturalHeight)
    const sourceX = (image.naturalWidth - sourceSize) / 2
    const sourceY = (image.naturalHeight - sourceSize) / 2
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    canvas.getContext('2d').drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, size, size)
    canvas.toBlob((blob) => {
      if (!blob) {
        reject({ code: 'invalid_image' })
        return
      }
      resolve({ blob, name: isPng ? 'avatar.png' : 'avatar.jpg' })
    }, isPng ? 'image/png' : 'image/jpeg', isPng ? undefined : 0.9)
  }
  image.onerror = () => {
    URL.revokeObjectURL(url)
    reject({ code: 'invalid_image' })
  }
  image.src = url
})

/**
 * Секция «Настройки профиля» для страницы /settings:
 * уровень доступа (hidden/partial/public) + редактор цветов с живым предпросмотром.
 * Данные — свой профиль из useProfileStore (страница настроек загружает его при монтировании).
 */
const ProfileCustomization = () => {
  const { t } = useTranslation()
  const { isTelegramMiniApp } = useAppEnvironment()
  const {
    profile, isLoading, error, loadProfile: reloadProfile, setVisibility, saveColors,
    setTelegramShow, isSavingSettings, uploadBackground, saveBackgroundSettings,
    deleteBackground, isUploadingBackground, uploadAvatar, deleteAvatar, isUpdatingAvatar
  } = useProfileStore()
  // draft инициализируется эффектом после загрузки профиля (null = переинициализировать)
  const [draft, setDraft] = useState(null)
  // Настройки фона карточки: draft редактора + бейзлайн сохранённого
  const [bgDraft, setBgDraft] = useState(null)
  const [isBgDeleteOpen, setIsBgDeleteOpen] = useState(false)
  const [isBgDeleting, setIsBgDeleting] = useState(false)
  const [isVisibilitySaving, setIsVisibilitySaving] = useState(false)
  const [isContactsSaving, setIsContactsSaving] = useState(false)
  const fileInputRef = useRef(null)
  const avatarInputRef = useRef(null)
  // Что сохранено на сервере: пока картинки нет, настройки в ответе профиля
  // не видны — бейзлайн ведём локально и обновляем после каждого успешного сейва
  const bgSavedRef = useRef(null)

  useEffect(() => {
    if (profile?.is_owner && !draft) {
      setDraft(fromColors(profile.colors))
      setBgDraft(fromBackground(profile.background))
      bgSavedRef.current = fromBackground(profile.background)
    }
  }, [profile, draft])

  // Свежие значения для сохранения при размонтировании
  const latestRef = useRef(null)
  useEffect(() => {
    latestRef.current = { draft, profile, bgDraft, bgSavedRef }
  }, [draft, profile, bgDraft])

  // Автосохранение цветов: короткий debounce после последнего изменения.
  // Колорпикер при перетаскивании и слайдер угла генерируют поток onChange,
  // поэтому запрос уходит один — когда пользователь закончил ввод.
  useEffect(() => {
    if (!draft || profile?.is_owner !== true || isSavingSettings) return

    const timer = setTimeout(async () => {
      const payload = buildColorsPayload(draft, fromColors(profile.colors))
      if (!payload) return

      const res = await saveColors(payload)
      if (res.ok) {
        sendYandexGoal('profile-colors-save')
      } else {
        toast.error(t('publicProfile.colorsSaveError', 'Не удалось сохранить цвета'))
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [draft, profile, isSavingSettings, saveColors, t])

  // Автосохранение настроек фона — тот же debounce, отдельная JSON-ручка
  // (файл не участвует, перезаливки нет). Бейзлайн — bgSavedRef: пока
  // картинки нет, сервер настройки не показывает.
  useEffect(() => {
    if (!bgDraft || profile?.is_owner !== true || isSavingSettings) return

    const timer = setTimeout(async () => {
      const payload = buildBackgroundPayload(bgDraft, bgSavedRef.current)
      if (!payload) return

      const res = await saveBackgroundSettings(payload)
      if (res.ok) {
        bgSavedRef.current = { ...bgSavedRef.current, ...payload }
        sendYandexGoal('profile-bg-settings-save')
      } else {
        toast.error(t('publicProfile.bgSaveError', 'Не удалось сохранить настройки фона'))
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [bgDraft, profile, isSavingSettings, saveBackgroundSettings, t])

  // Уход с экрана до срабатывания debounce — сохраняем ещё не отправленное
  useEffect(() => () => {
    const latest = latestRef.current
    if (latest?.draft && latest.profile?.is_owner === true) {
      const payload = buildColorsPayload(latest.draft, fromColors(latest.profile.colors))
      if (payload) saveColors(payload)
    }
    if (latest?.bgDraft && latest.profile?.is_owner === true) {
      const payload = buildBackgroundPayload(latest.bgDraft, latest.bgSavedRef.current)
      if (payload) saveBackgroundSettings(payload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Кастомизация доступна только владельцу
  if (!profile || profile.is_owner !== true) {
    if (isLoading) {
      return (
        <div className="py-8 flex items-center justify-center">
          <Loader2 size={24} className="text-accent-primary animate-spin" />
        </div>
      )
    }

    // Ошибка загрузки — не прячем секцию молча, даём понять что сломалось
    if (error) {
      return (
        <div>
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Palette size={20} className="text-accent-primary" />
            {t('publicProfile.settingsTitle', 'Настройки профиля')}
          </h2>
          <div className="rounded-2xl border border-white/10 bg-surface p-5 text-center">
            <p className="text-sm text-text-secondary mb-3">
              {t('publicProfile.customizationLoadError', 'Не удалось загрузить настройки профиля')}
            </p>
            <button
              onClick={() => reloadProfile()}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold text-sm transition-all active:scale-[0.98]"
            >
              {t('publicProfile.retry', 'Повторить')}
            </button>
          </div>
        </div>
      )
    }

    // Профиль загружен, но это не свой профиль — секции быть не должно
    return null
  }

  if (!draft) {
    return (
      <div className="py-8 flex items-center justify-center">
        <Loader2 size={24} className="text-accent-primary animate-spin" />
      </div>
    )
  }

  const setKey = (key, value) => setDraft((d) => ({ ...d, [key]: value }))

  const visibilityOptions = [
    { value: 'hidden', icon: Lock, label: t('publicProfile.visibility.hidden', 'Скрытый'), description: t('publicProfile.visibilityHiddenDesc', 'Другие видят только имя, аватар, лайки и активность') },
    { value: 'partial', icon: Eye, label: t('publicProfile.visibility.partial', 'Частично открытый'), description: t('publicProfile.visibilityPartialDesc', 'Видно всё, кроме баланса и инвентаря') },
    { value: 'public', icon: Globe, label: t('publicProfile.visibility.public', 'Открытый'), description: t('publicProfile.visibilityPublicDesc', 'Видно всё, включая баланс и инвентарь') }
  ]

  const colorFields = [
    { key: 'nick', label: t('publicProfile.colorsNick', 'Цвет ника'), theme: PROFILE_THEME.text },
    { key: 'text_secondary', label: t('publicProfile.colorsTextSecondary', 'Цвет подписей'), theme: PROFILE_THEME.textSecondary },
    { key: 'accent', label: t('publicProfile.colorsAccent', 'Акцент'), theme: PROFILE_THEME.accent },
    { key: 'avatar_frame', label: t('publicProfile.colorsAvatarFrame', 'Рамка аватара'), theme: PROFILE_THEME.accent },
    { key: 'bg', label: t('publicProfile.colorsBg', 'Фон'), theme: PROFILE_THEME.bg },
    { key: 'bg2', label: t('publicProfile.colorsBg2', 'Второй цвет фона'), theme: PROFILE_THEME.surface },
    { key: 'content_bg', label: t('publicProfile.colorsContentBg', 'Фон остальных карточек'), theme: PROFILE_THEME.surface },
    { key: 'card_bg', label: t('publicProfile.colorsCardBg', 'Фон скинов'), theme: PROFILE_THEME.surface }
  ]

  const handleVisibility = async (value) => {
    if (value === profile.visibility || isVisibilitySaving) return
    setIsVisibilitySaving(true)
    const res = await setVisibility(value)
    setIsVisibilitySaving(false)

    if (res.ok) {
      hapticImpact('medium')
      sendYandexGoal('profile-visibility-change')
      toast.success(t('publicProfile.visibilitySaved', 'Настройка сохранена'))
    } else {
      toast.error(t('publicProfile.visibilityError', 'Не удалось изменить настройку'))
    }
  }

  // Контакт Telegram: ник есть у игроков мини-аппа и у веб-пользователей
  // с привязкой (тогда contacts !== null). Веб без Telegram — тумблер не нужен.
  const telegramUsername = profile.contacts?.telegram?.username ?? null
  const showTelegramToggle = isTelegramMiniApp || telegramUsername != null

  // Состояние тумблера — из show_telegram (владельцу contacts приходит
  // даже при выключенном переключателе — это предпросмотр, а не состояние)
  const handleTelegramToggle = async () => {
    if (isContactsSaving) return
    const want = profile.show_telegram ? 0 : 1
    setIsContactsSaving(true)
    const res = await setTelegramShow(want === 1)
    setIsContactsSaving(false)

    if (res.ok) {
      hapticImpact('medium')
      sendYandexGoal('profile-telegram-toggle')
    } else {
      toast.error(t('publicProfile.showTelegramError', 'Не удалось изменить настройку'))
    }
  }

  const handleResetAll = () => {
    // Сбросить всё — все восемь цветов со значением null (угол — дефолт);
    // уходит автосохранением как обычное изменение
    setDraft({
      nick: null, bg: null, bg2: null, bg_angle: 180, accent: null,
      avatar_frame: null, card_bg: null, content_bg: null, text_secondary: null
    })
  }

  const setBgKey = (key, value) => setBgDraft((d) => ({ ...d, [key]: value }))

  const AVATAR_UPLOAD_ERROR_KEYS = {
    no_file: 'avatarErrNoFile',
    invalid_format: 'avatarErrInvalidFormat',
    invalid_image: 'avatarErrInvalidImage',
    image_too_big: 'avatarErrTooBig',
    file_too_large: 'avatarErrFileLarge',
    rate_limit: 'avatarErrRateLimit',
    avatar_banned: 'avatarErrBanned',
    avatar_disabled: 'avatarErrDisabled',
    cdn_error: 'avatarErrCdn'
  }

  const handleAvatarFile = async (file) => {
    if (!file || isUpdatingAvatar) return

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      toast.error(t('publicProfile.avatarErrInvalidFormat', 'Подходят только JPG и PNG'))
      if (avatarInputRef.current) avatarInputRef.current.value = ''
      return
    }

    let prepared
    try {
      const size = Math.max(1, Math.round(Number(profile.avatar_upload?.size) || 88))
      prepared = await prepareAvatarFile(file, size)
    } catch {
      toast.error(t('publicProfile.avatarErrInvalidImage', 'Не удалось прочитать картинку'))
      if (avatarInputRef.current) avatarInputRef.current.value = ''
      return
    }

    const res = await uploadAvatar(prepared)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('profile-avatar-upload')
    } else if (res) {
      const key = res.status === 413
        ? 'avatarErrFileLarge'
        : (AVATAR_UPLOAD_ERROR_KEYS[res.error] || 'avatarErrDefault')
      toast.error(t(`publicProfile.${key}`, {
        defaultValue: 'Не удалось загрузить аватар, попробуй позже',
        count: profile.avatar_upload?.daily_limit ?? 0
      }))
    }

    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  const handleAvatarDelete = async () => {
    if (isUpdatingAvatar) return
    const res = await deleteAvatar()
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('profile-avatar-delete')
    } else if (res) {
      toast.error(t('publicProfile.avatarDeleteError', 'Не удалось вернуть аватар Telegram'))
    }
  }

  // Коды ошибок загрузки → ключи переводов (413 от nginx приходит без JSON)
  const BG_UPLOAD_ERROR_KEYS = {
    invalid_format: 'bgErrInvalidFormat',
    invalid_image: 'bgErrInvalidImage',
    image_too_big: 'bgErrTooBig',
    file_too_large: 'bgErrFileLarge',
    rate_limit: 'bgErrRateLimit',
    background_banned: 'bgErrBanned',
    background_disabled: 'bgErrDisabled',
    cdn_error: 'bgErrCdn'
  }

  /**
   * Загрузка картинки-фона: клиент уменьшает её до 500 px (canvas), чтобы
   * пользователь не ловил отказы сервера, и отправляет вместе с текущими
   * настройками редактора. Ответ — свежий профиль, синхронизируем draft.
   */
  const handleBgFile = async (file) => {
    if (!file || isUploadingBackground) return

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      toast.error(t('publicProfile.bgErrInvalidFormat', 'Подходят только JPG и PNG'))
      return
    }

    let prepared
    try {
      prepared = await prepareBackgroundFile(file)
    } catch (e) {
      const key = e?.code === 'file_too_large' ? 'bgErrFileLarge' : 'bgErrInvalidImage'
      toast.error(t(`publicProfile.${key}`, 'Не удалось прочитать картинку'))
      return
    }

    const res = await uploadBackground(prepared, bgDraft)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('profile-bg-upload')
      const fresh = fromBackground(res.background)
      setBgDraft(fresh)
      bgSavedRef.current = fresh
    } else if (res) {
      const key = res.status === 413
        ? 'bgErrFileLarge'
        : (BG_UPLOAD_ERROR_KEYS[res.error] || 'bgErrDefault')
      toast.error(t(`publicProfile.${key}`, 'Не удалось загрузить картинку, попробуй позже'))
    }

    // Сброс input, чтобы повторный выбор того же файла вызвал onChange
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Снятие фона: бэкенд сбрасывает и настройки отображения — редактор тоже
  const handleBgDelete = async () => {
    if (isBgDeleting) return
    setIsBgDeleting(true)
    const res = await deleteBackground()
    setIsBgDeleting(false)

    if (res?.ok) {
      setIsBgDeleteOpen(false)
      hapticNotify('success')
      sendYandexGoal('profile-bg-delete')
      const fresh = fromBackground(null)
      setBgDraft(fresh)
      bgSavedRef.current = fresh
    } else if (res) {
      toast.error(t('publicProfile.bgDeleteError', 'Не удалось удалить фон'))
    }
  }

  const previewName = `${profile.firstname || ''} ${profile.lastname || ''}`.trim() ||
    t('publicProfile.player', 'Игрок')

  // URL фоновой картинки для превью (настройки берём из bgDraft — живой отклик)
  const bgPreviewUrl = typeof profile.background?.url === 'string' ? profile.background.url : null
  const previewContentBg = contentBgColor(draft)
  const previewPrimary = cardPrimaryColor(draft)
  const previewSecondary = cardSecondaryColor(draft)
  const previewNick = cardNickColor(draft)
  const previewAccent = accentColor(draft)
  const previewFrame = isValidHex(draft.avatar_frame) ? draft.avatar_frame : previewAccent
  const previewBorder = `${previewPrimary}1F`
  const previewSurface = `${previewPrimary}0D`
  const previewSkinBg = skinCardBgColor(draft)
  const previewSkinPrimary = skinCardPrimaryColor(draft)

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
        <Palette size={20} className="text-accent-primary" />
        {t('publicProfile.settingsTitle', 'Настройки профиля')}
      </h2>

      <div className="rounded-2xl border border-white/10 bg-surface p-5 space-y-6">
        {/* Видимость */}
        <div>
          <h4 className="text-sm font-semibold text-text-secondary uppercase mb-3">
            {t('publicProfile.visibilityTitle', 'Кто видит профиль')}
          </h4>
          <div className="space-y-2">
            {visibilityOptions.map((option) => {
              const Icon = option.icon
              const isSelected = profile.visibility === option.value
              return (
                <button
                  key={option.value}
                  onClick={() => handleVisibility(option.value)}
                  disabled={isVisibilitySaving}
                  className={`w-full p-3.5 rounded-xl border text-left transition-all active:scale-[0.98] ${
                    isSelected
                      ? 'bg-accent-primary/15 border-accent-primary'
                      : 'bg-dark-primary border-white/10 hover:border-accent-primary/30'
                  } disabled:opacity-60`}
                >
                  <div className="flex items-center gap-3">
                    {isVisibilitySaving && isSelected ? (
                      <Loader2 size={18} className="text-accent-primary animate-spin shrink-0" />
                    ) : (
                      <Icon size={18} className="text-accent-primary shrink-0" />
                    )}
                    <div>
                      <div className="font-medium text-white text-sm">{option.label}</div>
                      <div className="text-xs text-text-secondary">{option.description}</div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {profile.avatar_upload && (
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase mb-3 flex items-center gap-2">
              <User size={14} className="text-accent-primary" />
              {t('publicProfile.avatarTitle', 'Аватар профиля')}
            </h4>
            <div className="flex items-center gap-3">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-accent-primary/60 bg-dark-primary">
                {profile.ava ? (
                  <img src={profile.ava} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <User size={24} className="text-text-secondary" />
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {profile.avatar_upload.daily_limit > 0 && (
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={!profile.avatar_upload.can_upload || isUpdatingAvatar}
                    className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-2.5 text-sm font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
                  >
                    {isUpdatingAvatar ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {t('publicProfile.avatarUpload', 'Загрузить аватар')}
                  </button>
                )}
                {profile.avatar_upload.custom && (
                  <button
                    type="button"
                    onClick={handleAvatarDelete}
                    disabled={isUpdatingAvatar}
                    className="flex items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-medium text-white transition-all active:scale-[0.98] hover:border-accent-primary/40 disabled:opacity-50"
                  >
                    <RotateCcw size={14} />
                    {t('publicProfile.avatarTelegram', 'Вернуть аватар Telegram')}
                  </button>
                )}
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={(e) => handleAvatarFile(e.target.files?.[0])}
              />
            </div>
            {profile.avatar_upload.daily_limit > 0 && (
              <div className="mt-3 space-y-1 text-xs text-text-secondary">
                <p>
                  {t('publicProfile.avatarHint', {
                    size: profile.avatar_upload.size ?? 88,
                    defaultValue: 'Загружайте квадратное изображение JPG или PNG до 1 МБ. Картинка будет уменьшена до {{size}}×{{size}}, края неквадратного изображения обрежутся.'
                  })}
                </p>
                <p className="hidden tabular-nums">
                  {profile.avatar_upload.banned
                    ? t('publicProfile.avatarErrBanned', 'Загрузка аватара для вашего аккаунта отключена')
                    : t('publicProfile.avatarLeftToday', {
                        count: profile.avatar_upload.left_today,
                        defaultValue: 'Осталось загрузок сегодня: {{count}}'
                      })}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Контакт Telegram: ссылку бэкенд берёт из ника Telegram сам,
            вручную вводить нечего — только показывать/скрывать */}
        {showTelegramToggle && (
          <div>
            <h4 className="text-sm font-semibold text-text-secondary uppercase mb-3">
              {t('publicProfile.contactsTitle', 'Контакт')}
            </h4>
            <button
              type="button"
              role="switch"
              aria-checked={profile.show_telegram === true}
              onClick={handleTelegramToggle}
              disabled={isContactsSaving}
              className="w-full p-3.5 rounded-xl border bg-dark-primary border-white/10 text-left transition-all active:scale-[0.98] disabled:opacity-60"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {isContactsSaving ? (
                      <Loader2 size={16} className="text-accent-primary animate-spin shrink-0" />
                    ) : (
                      <Send size={16} className="text-accent-primary shrink-0" />
                    )}
                    <span className="font-medium text-white text-sm">
                      {t('publicProfile.showTelegram', 'Показывать мой Telegram')}
                    </span>
                  </div>
                  {telegramUsername ? (
                    <div className="mt-1 text-xs text-text-secondary truncate">
                      <span className={profile.show_telegram ? 'text-accent-primary font-medium' : ''}>
                        @{telegramUsername}
                      </span>
                      {!profile.show_telegram && (
                        <> · {t('publicProfile.showTelegramHint', 'Другие увидят эту ссылку')}</>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-text-secondary">
                      {t('publicProfile.showTelegramNoNick', 'Задай ник в настройках Telegram — ссылка появится сама')}
                    </div>
                  )}
                </div>
                <span
                  className={`relative w-10 h-6 rounded-full shrink-0 transition-colors ${
                    profile.show_telegram ? 'bg-accent-primary' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      profile.show_telegram ? 'translate-x-4' : ''
                    }`}
                  />
                </span>
              </div>
            </button>
          </div>
        )}

        {/* Цвета */}
        <div>
          <h4 className="text-sm font-semibold text-text-secondary uppercase mb-3 flex items-center gap-2">
            {t('publicProfile.colors', 'Цвета профиля')}
            {isSavingSettings && (
              <Loader2 size={14} className="animate-spin text-accent-primary" />
            )}
          </h4>

          {/* Живой предпросмотр */}
          <div className="mb-4 space-y-2 rounded-2xl p-3" style={{ background: profileBackground(draft) }}>
            <div
              className="relative overflow-hidden rounded-xl border p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              style={{ backgroundColor: previewContentBg, borderColor: previewBorder }}
            >
              {bgPreviewUrl && bgDraft && (
                <>
                  <div
                    className="pointer-events-none absolute"
                    style={backgroundImageStyle({ ...bgDraft, url: bgPreviewUrl })}
                  />
                  <div className="pointer-events-none absolute inset-0" style={backgroundOverlayStyle(bgDraft)} />
                </>
              )}
              <div className="relative flex flex-col items-center text-center">
                <div className="flex w-full items-center justify-between text-[9px]" style={{ color: previewSecondary }}>
                  <span className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: previewAccent }} />
                    {t('publicProfile.online', 'Онлайн')}
                  </span>
                  <span>LVL {profile.level ?? 0}</span>
                </div>
                <div
                  className="mt-1 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2"
                  style={{ borderColor: previewFrame, boxShadow: `0 0 12px ${previewFrame}44` }}
                >
                  {profile.ava ? (
                    <img src={profile.ava} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Heart size={18} style={{ color: previewAccent }} />
                  )}
                </div>
                <p className="mt-1.5 max-w-full truncate text-sm font-bold" style={{ color: previewNick }}>
                  {previewName}
                </p>
                <p className="mt-0.5 text-[9px]" style={{ color: previewSecondary }}>
                  {t('publicProfile.previewCaption', 'Так будет выглядеть профиль')}
                </p>
                <div className="mt-2 grid w-full grid-cols-2 gap-1">
                  <span
                    className="flex items-center justify-center gap-1 rounded-lg border px-1 py-1.5 text-[9px] font-semibold"
                    style={{ color: previewSecondary, backgroundColor: previewSurface, borderColor: previewBorder }}
                  >
                    <Heart size={10} style={{ color: previewAccent }} />
                    {profile.likes ?? 0}
                  </span>
                  <span
                    className="flex items-center justify-center gap-1 rounded-lg border px-1 py-1.5 text-[9px] font-semibold"
                    style={{ color: previewSecondary, backgroundColor: `${previewAccent}14`, borderColor: `${previewAccent}55` }}
                  >
                    <Send size={10} style={{ color: previewAccent }} />
                    Telegram
                  </span>
                </div>
              </div>
            </div>

            <div
              className="rounded-xl border p-3"
              style={{ backgroundColor: previewContentBg, borderColor: previewBorder }}
            >
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase" style={{ color: previewPrimary }}>
                <Gem size={11} style={{ color: previewAccent }} />
                {t('publicProfile.balance', 'Баланс')}
              </p>
              <p className="mt-1 text-sm font-bold tabular-nums" style={{ color: previewAccent }}>
                {profile.balance ?? 0}
              </p>
            </div>

            <div
              className="rounded-xl border p-3"
              style={{ backgroundColor: previewContentBg, borderColor: previewBorder }}
            >
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase" style={{ color: previewPrimary }}>
                <Package size={11} style={{ color: previewAccent }} />
                {t('publicProfile.topSkins', 'Топ моих скинов')}
              </p>
              <div
                className="rounded-lg border p-2"
                style={{ backgroundColor: previewSkinBg, borderColor: `${previewSkinPrimary}1F` }}
              >
                <p className="truncate text-[10px] font-semibold" style={{ color: previewSkinPrimary }}>
                  AK-47 | Redline
                </p>
                <p className="mt-1 flex items-center gap-1 text-[9px]" style={{ color: previewSkinPrimary }}>
                  <Gem size={9} /> 1 250
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2.5">
            {colorFields.map((field) => (
              <div key={field.key} className="flex items-center justify-between gap-3">
                <span className="text-sm text-white">{field.label}</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="color"
                    value={draft[field.key] ?? field.theme}
                    onChange={(e) => setKey(field.key, e.target.value.toUpperCase())}
                    className="w-10 h-10 rounded-lg cursor-pointer bg-transparent p-0.5 border border-white/20"
                    aria-label={field.label}
                  />
                  <input
                    type="text"
                    value={draft[field.key] ?? ''}
                    onChange={(e) => setKey(field.key, e.target.value.toUpperCase() || null)}
                    placeholder={t('publicProfile.themeDefault', 'тема')}
                    className="w-24 bg-dark-primary border border-white/10 rounded-lg px-2 py-2 text-xs text-white focus:border-accent-primary focus:outline-none uppercase"
                    maxLength={7}
                    aria-label={`${field.label} hex`}
                  />
                  <button
                    type="button"
                    onClick={() => setKey(field.key, null)}
                    disabled={!draft[field.key]}
                    className="p-2 rounded-lg text-text-secondary hover:text-white transition-colors disabled:opacity-30"
                    aria-label={t('publicProfile.colorsResetOne', 'Сбросить')}
                  >
                    <RotateCcw size={14} />
                  </button>
                </div>
              </div>
            ))}

            {/* Угол градиента — имеет смысл только при заданном bg2.
                Числовой ввод 0–359 с быстрыми пресетами */}
            <div className={isValidHex(draft.bg2) ? '' : 'opacity-40'}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white shrink-0">{t('publicProfile.colorsBgAngle', 'Угол градиента')}</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={0}
                    max={359}
                    inputMode="numeric"
                    value={draft.bg_angle}
                    disabled={!isValidHex(draft.bg2)}
                    onChange={(e) => {
                      if (e.target.value === '') return
                      const v = Math.round(Number(e.target.value))
                      if (Number.isFinite(v)) setKey('bg_angle', Math.min(359, Math.max(0, v)))
                    }}
                    className="w-20 bg-dark-primary border border-white/10 rounded-lg px-2 py-2 text-xs text-white text-right tabular-nums focus:border-accent-primary focus:outline-none disabled:opacity-50"
                    aria-label={t('publicProfile.colorsBgAngle', 'Угол градиента')}
                  />
                  <span className="text-xs text-text-secondary">°</span>
                </div>
              </div>
              {/* Пресеты частых углов */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                  <button
                    key={angle}
                    type="button"
                    disabled={!isValidHex(draft.bg2)}
                    onClick={() => setKey('bg_angle', angle)}
                    className={`px-2 py-1 rounded-md border text-[11px] tabular-nums transition-all active:scale-[0.98] disabled:opacity-50 ${
                      draft.bg_angle === angle
                        ? 'border-accent-primary/60 bg-accent-primary/10 text-white'
                        : 'border-white/10 bg-dark-primary text-text-secondary hover:text-white'
                    }`}
                  >
                    {angle}°
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Фон карточки — картинка + настройки отображения */}
        <div>
          <h4 className="text-sm font-semibold text-text-secondary uppercase mb-3 flex items-center gap-2">
            <ImageIcon size={14} className="text-accent-primary" />
            {t('publicProfile.bgTitle', 'Фон карточки')}
            {isSavingSettings && (
              <Loader2 size={14} className="animate-spin text-accent-primary" />
            )}
          </h4>

          {/* Текущая картинка + загрузка/удаление */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative w-16 h-16 shrink-0 rounded-xl border border-white/10 bg-dark-primary overflow-hidden flex items-center justify-center">
              {bgPreviewUrl ? (
                <img src={bgPreviewUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={22} className="text-text-secondary" />
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingBackground}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-60"
              >
                {isUploadingBackground ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Upload size={16} />
                )}
                {bgPreviewUrl
                  ? t('publicProfile.bgReplace', 'Заменить картинку')
                  : t('publicProfile.bgUpload', 'Загрузить картинку')}
              </button>
              {bgPreviewUrl && (
                <button
                  type="button"
                  onClick={() => setIsBgDeleteOpen(true)}
                  disabled={isBgDeleting || isUploadingBackground}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-error/40 text-error text-sm font-medium transition-all active:scale-[0.98] hover:bg-error/10 disabled:opacity-60"
                >
                  {isBgDeleting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  {t('publicProfile.bgDelete', 'Удалить фон')}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={(e) => handleBgFile(e.target.files?.[0])}
            />
          </div>
          <p className="text-xs text-text-secondary mb-4">
            {t('publicProfile.bgHint', 'JPG или PNG, до 500×500 px и 1 МБ. Более крупные картинки уменьшаются автоматически.')}
          </p>

          {/* Настройки отображения — при загруженной картинке */}
          {bgPreviewUrl && bgDraft && (
            <div className="space-y-4">
              {/* Заполнение — как картинка занимает карточку */}
              <div>
                <span className="text-sm text-white">{t('publicProfile.bgFit', 'Заполнение')}</span>
                <div className="mt-2 grid grid-cols-4 gap-1.5">
                  {BG_FITS.map((fit) => (
                    <button
                      key={fit}
                      type="button"
                      onClick={() => {
                        hapticSelectionChanged()
                        setBgKey('fit', fit)
                      }}
                      className={`px-1 py-2 rounded-lg border text-xs transition-all active:scale-[0.98] ${
                        bgDraft.fit === fit
                          ? 'border-accent-primary/60 bg-accent-primary/10 text-white'
                          : 'border-white/10 bg-dark-primary text-text-secondary hover:text-white'
                      }`}
                    >
                      {t(`publicProfile.bgFit_${fit}`, fit)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Положение — какая часть картинки видна при обрезке
                  (не имеет смысла при «Растянуть» — карта на всю карточку) */}
              <div className={bgDraft.fit === 'stretch' ? 'opacity-40' : ''}>
                <span className="text-sm text-white">{t('publicProfile.bgPosition', 'Положение')}</span>
                <div className="mt-2 flex flex-col items-start gap-1.5">
                  {[0, 50, 100].map((y) => (
                    <div key={y} className="flex gap-1.5">
                      {[0, 50, 100].map((x) => {
                        const active = bgDraft.position_x === x && bgDraft.position_y === y
                        return (
                          <button
                            key={`${x}-${y}`}
                            type="button"
                            disabled={bgDraft.fit === 'stretch'}
                            onClick={() => {
                              hapticSelectionChanged()
                              setBgDraft((d) => ({ ...d, position_x: x, position_y: y }))
                            }}
                            className={`w-8 h-8 rounded-lg border transition-all active:scale-[0.98] disabled:opacity-50 ${
                              active
                                ? 'border-accent-primary bg-accent-primary/20'
                                : 'border-white/10 bg-dark-primary hover:border-accent-primary/30'
                            }`}
                            aria-label={`${x}%, ${y}%`}
                          >
                            {active && (
                              <span className="block w-2 h-2 rounded-full bg-accent-primary mx-auto" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Затемнение — читаемость текста на карточке */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-white">{t('publicProfile.bgOverlay', 'Затемнение')}</span>
                  <span className="text-xs text-text-secondary tabular-nums">{bgDraft.overlay}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={bgDraft.overlay}
                  onChange={(e) => setBgKey('overlay', Number(e.target.value))}
                  className="w-full accent-accent-primary"
                  aria-label={t('publicProfile.bgOverlay', 'Затемнение')}
                />
              </div>

              {/* Размытие слоя картинки */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-white">{t('publicProfile.bgBlur', 'Размытие')}</span>
                  <span className="text-xs text-text-secondary tabular-nums">{bgDraft.blur} px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={20}
                  value={bgDraft.blur}
                  onChange={(e) => setBgKey('blur', Number(e.target.value))}
                  className="w-full accent-accent-primary"
                  aria-label={t('publicProfile.bgBlur', 'Размытие')}
                />
              </div>
            </div>
          )}
        </div>

        {/* Сброс всех цветов к теме — уходит автосохранением */}
        <button
          onClick={handleResetAll}
          disabled={isSavingSettings}
          className="w-full py-3 bg-transparent border border-text-secondary text-text-secondary rounded-xl hover:bg-text-secondary/10 transition-all active:scale-[0.98] disabled:opacity-50"
        >
          {t('publicProfile.colorsReset', 'Сбросить всё')}
        </button>
      </div>

      {/* Удаление фона — подтверждение: вернуть картинку будет стоить
          одну попытку дневного лимита загрузок */}
      <ConfirmModal
        isOpen={isBgDeleteOpen}
        onClose={() => !isBgDeleting && setIsBgDeleteOpen(false)}
        onConfirm={handleBgDelete}
        title={t('publicProfile.bgDeleteTitle', 'Удалить фон?')}
        message={t('publicProfile.bgDeleteMessage', 'Картинка удалится, настройки сбросятся к стандартным. Загрузить новую можно сразу, но загрузок в сутки ограниченное количество.')}
        confirmText={t('publicProfile.bgDelete', 'Удалить фон')}
        isLoading={isBgDeleting}
      />
    </div>
  )
}

export default ProfileCustomization
